<script lang="ts">
    import { formatDateLocale } from '@/lib/dateUtils';
    import { History, Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-svelte';
    import type { AuditLog } from '@/types/types';
    import * as Card from '@/components/ui/card';
    import { SvelteSet } from 'svelte/reactivity';

    type Props = {
        auditLogs?: AuditLog[];
        id?: string;
    };

    let { auditLogs = [], id }: Props = $props();

    // Track which audit logs have their changes expanded
    let expandedLogs = new SvelteSet<number>();

    function toggleExpanded(logId: number) {
        if (expandedLogs.has(logId)) {
            expandedLogs.delete(logId);
        } else {
            expandedLogs.add(logId);
        }
    }

    function getEventConfig(event: AuditLog['event']) {
        switch (event) {
            case 'created':
                return {
                    icon: Plus,
                    label: 'Created',
                    class: 'event-created'
                };
            case 'updated':
                return {
                    icon: Pencil,
                    label: 'Updated',
                    class: 'event-updated'
                };
            case 'deleted':
                return {
                    icon: Trash2,
                    label: 'Deleted',
                    class: 'event-deleted'
                };
            default:
                return {
                    icon: History,
                    label: event,
                    class: 'event-default'
                };
        }
    }
</script>

<Card.Root {id}>
    <Card.Content>
        <div class="audit-log-section">
            <Card.Title class="flex items-center gap-2 mb-4 text-lg">
                <History size={20} />
                Audit Log ({auditLogs.length})
            </Card.Title>

            {#if auditLogs.length === 0}
                <div class="empty-state">
                    <History size={48} class="empty-icon" />
                    <p>No audit log recorded yet</p>
                </div>
            {:else}
                <div class="timeline">
                    {#each auditLogs as auditLog (auditLog.id)}
                        {@const config = getEventConfig(auditLog.event)}
                        <div class="timeline-item">
                            <div class="timeline-marker {config.class}">
                                <svelte:component this={config.icon} size={14} />
                            </div>
                            <div class="timeline-content">
                                <div class="timeline-header">
                                    <span class="event-badge {config.class}">{config.label}</span>
                                    <span class="timestamp">{formatDateLocale(auditLog.created_at)}</span>
                                </div>
                                {#if auditLog.causer}
                                    <p class="causer">by {auditLog.causer.name}</p>
                                {/if}

                                {#if auditLog.event === 'updated' && auditLog.changes.length > 0}
                                    <button
                                        class="changes-toggle"
                                        onclick={() => toggleExpanded(auditLog.id)}
                                        type="button"
                                    >
                                        {#if expandedLogs.has(auditLog.id)}
                                            <ChevronDown size={16} />
                                        {:else}
                                            <ChevronRight size={16} />
                                        {/if}
                                        {auditLog.changes.length} field{auditLog.changes.length !== 1 ? 's' : ''} changed
                                    </button>

                                    {#if expandedLogs.has(auditLog.id)}
                                        <div class="changes-list">
                                            {#each auditLog.changes as change (change.field_key)}
                                                <div class="change-item">
                                                    <span class="field-name">{change.field}</span>
                                                    <div class="change-values">
                                                        <span class="old-value" title={change.old_value ?? 'Empty'}>
                                                            {change.old_value ?? '(empty)'}
                                                        </span>
                                                        <span class="arrow">&rarr;</span>
                                                        <span class="new-value" title={change.new_value ?? 'Empty'}>
                                                            {change.new_value ?? '(empty)'}
                                                        </span>
                                                    </div>
                                                </div>
                                            {/each}
                                        </div>
                                    {/if}
                                {/if}
                            </div>
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
    </Card.Content>
</Card.Root>

<style lang="scss">
    .audit-log-section {
        background: var(--clr-surface-2);
        border-radius: 8px;
        border: 1px solid var(--clr-border);
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 1rem;
        color: var(--clr-on-surface-3);
        gap: 1rem;

        :global(.empty-icon) {
            opacity: 0.5;
        }

        p {
            font-style: italic;
            margin: 0;
        }
    }

    .timeline {
        display: flex;
        flex-direction: column;
        gap: 0;
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: 11px;
            top: 24px;
            bottom: 24px;
            width: 2px;
            background: var(--clr-border);
        }
    }

    .timeline-item {
        display: flex;
        gap: 1rem;
        padding: 0.75rem 0;
        position: relative;

        &:first-child {
            padding-top: 0;
        }

        &:last-child {
            padding-bottom: 0;
        }
    }

    .timeline-marker {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        z-index: 1;
        background: var(--clr-surface-1);
        border: 2px solid var(--clr-border);
        color: var(--clr-on-surface-3);

        &.event-created {
            border-color: var(--success);
            color: var(--success);
            background: color-mix(in srgb, var(--success) 10%, transparent);
        }

        &.event-updated {
            border-color: var(--chart-2);
            color: var(--chart-2);
            background: color-mix(in srgb, var(--chart-2) 10%, transparent);
        }

        &.event-deleted {
            border-color: var(--destructive);
            color: var(--destructive);
            background: color-mix(in srgb, var(--destructive) 10%, transparent);
        }
    }

    .timeline-content {
        flex: 1;
        min-width: 0;
    }

    .timeline-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
    }

    .event-badge {
        font-size: 0.75rem;
        font-weight: 600;
        padding: 0.125rem 0.5rem;
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.05em;

        &.event-created {
            background: color-mix(in srgb, var(--success) 15%, transparent);
            color: var(--success);
        }

        &.event-updated {
            background: color-mix(in srgb, var(--chart-2) 15%, transparent);
            color: var(--chart-2);
        }

        &.event-deleted {
            background: color-mix(in srgb, var(--destructive) 15%, transparent);
            color: var(--destructive);
        }

        &.event-default {
            background: var(--clr-surface-3);
            color: var(--clr-on-surface-2);
        }
    }

    .timestamp {
        font-size: 0.8rem;
        color: var(--clr-on-surface-3);
    }

    .causer {
        font-size: 0.875rem;
        color: var(--clr-on-surface-2);
        margin: 0.25rem 0 0;
    }

    .changes-toggle {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        margin-top: 0.5rem;
        padding: 0.25rem 0.5rem;
        font-size: 0.8rem;
        color: var(--clr-on-surface-2);
        background: var(--clr-surface-3);
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background 200ms;

        &:hover {
            background: var(--clr-surface-4, var(--clr-border));
        }
    }

    .changes-list {
        margin-top: 0.5rem;
        padding: 0.75rem;
        background: var(--clr-surface-1);
        border: 1px solid var(--clr-border);
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .change-item {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .field-name {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--clr-on-surface-2);
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .change-values {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        flex-wrap: wrap;
    }

    .old-value {
        color: var(--destructive);
        text-decoration: line-through;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .arrow {
        color: var(--clr-on-surface-3);
        flex-shrink: 0;
    }

    .new-value {
        color: var(--success);
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
</style>